"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download,
  LineChart,
  Package,
  PackagePlus,
  Pencil,
  Plus,
  LayoutGrid,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Badge } from "@/shadcn-components/ui/badge";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcn-components/ui/tabs";
import { cn } from "@/lib/utils";
import { getFile, projectToRecord } from "@/lib/custom-indicators/db";
import { DEFAULT_README_FILE } from "@/lib/custom-indicators/project-fs";
import { loadPersistedBuildIntoRuntime } from "@/lib/custom-indicators/hydrate-build";
import type { CreateProjectDraft } from "@/lib/custom-indicators/create-draft";
import {
  buildOwnershipTags,
  isProjectEditable,
  normalizeProject,
  ownerDisplayLabel,
  sourceDisplayLabel,
} from "@/lib/custom-indicators/project-ownership";
import { useIndicatorCompileStore } from "@/lib/custom-indicators/runtime/compile-store";
import {
  buildStudioCatalog,
  buildTemplateReadme,
  defaultCatalogSelection,
  filterCatalogBySection,
  formatBuildProvenance,
  formatBytes,
  formatCatalogDate,
  searchCatalogItems,
  sectionCount,
  type StudioCatalogFilter,
  type StudioCatalogItem,
} from "@/lib/custom-indicators/studio-catalog";
import type { CompileInputMeta } from "@/lib/custom-indicators/runtime-types";
import type {
  CustomIndicatorBuild,
  CustomIndicatorProject,
  CustomIndicatorRecord,
} from "@/lib/custom-indicators/types";
import CustomIndicatorIcon from "./CustomIndicatorIcon";
import CustomIndicatorPreviewChart from "./CustomIndicatorPreviewChart";
import { StudioIndicatorReadme } from "./StudioIndicatorReadme";

const FILTER_OPTIONS: Array<{
  id: StudioCatalogFilter;
  label: string;
  icon: typeof Package;
}> = [
  { id: "installed", label: "Installed", icon: Package },
  { id: "my", label: "My", icon: Sparkles },
  { id: "marketplace", label: "Marketplace", icon: LayoutGrid },
];

const SQUARE_ICON_CLASS = "h-14 w-14 shrink-0 rounded-md";

function ownershipBadgeLabel(tag: string): string {
  const labels: Record<string, string> = {
    system: "System",
    external: "External",
    imported: "Imported",
    "created-by-you": "Created by you",
    "binary-only": "Binary only",
    "source-available": "Source available",
    indicator: "Indicator",
    algo: "Algo",
    overlay: "Overlay",
    pane: "Pane",
    "read-only": "Read-only",
  };
  return labels[tag] ?? tag;
}

function canShowCreateFromTemplate(item: StudioCatalogItem): boolean {
  if (item.kind === "template") return true;
  const project = item.project;
  if (!project?.sourceTemplateId || !project.hasSource) return false;
  return project.source === "marketplace" || project.owner === "system";
}

export interface StudioIndicatorBrowserProps {
  projects: CustomIndicatorProject[];
  buildsByProject: Record<string, CustomIndicatorBuild>;
  isLoading: boolean;
  error: string | null;
  onNew: () => void;
  onImportFile: (file: File) => void;
  onAddToChart: (record: CustomIndicatorRecord) => void;
  onOpenEditor: (project: CustomIndicatorProject) => void;
  onExportSource: (projectId: string) => void;
  onExportBinary: (projectId: string) => void;
  canExportSource: (project: CustomIndicatorProject) => boolean;
  onDelete: (project: CustomIndicatorProject) => void;
  onCreateFromTemplate: (draft: CreateProjectDraft) => void;
  onInstallTemplate: (templateId: string) => void;
}

export default function StudioIndicatorBrowser({
  projects,
  buildsByProject,
  isLoading,
  error,
  onNew,
  onImportFile,
  onAddToChart,
  onOpenEditor,
  onExportSource,
  onExportBinary,
  canExportSource,
  onDelete,
  onCreateFromTemplate,
  onInstallTemplate,
}: StudioIndicatorBrowserProps) {
  const catalog = useMemo(
    () => buildStudioCatalog(projects, buildsByProject),
    [projects, buildsByProject]
  );

  const installedTemplateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of projects) {
      if (p.sourceTemplateId) ids.add(p.sourceTemplateId);
    }
    return ids;
  }, [projects]);

  const [filter, setFilter] = useState<StudioCatalogFilter>("my");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState("details");
  const [readmeMarkdown, setReadmeMarkdown] = useState<string>("");
  const [readmeLoading, setReadmeLoading] = useState(false);
  const [installingTemplateId, setInstallingTemplateId] = useState<string | null>(
    null
  );

  const filteredItems = useMemo(() => {
    const section = filterCatalogBySection(catalog, filter);
    return searchCatalogItems(section, search);
  }, [catalog, filter, search]);

  const selected = useMemo(
    () => catalog.find((i) => i.id === selectedId) ?? null,
    [catalog, selectedId]
  );

  useEffect(() => {
    if (selectedId && filteredItems.some((i) => i.id === selectedId)) return;
    const next =
      filteredItems[0]?.id ?? defaultCatalogSelection(catalog, filter);
    setSelectedId(next);
  }, [catalog, filter, filteredItems, selectedId]);

  useEffect(() => {
    if (!selected) {
      setReadmeMarkdown("");
      return;
    }
    if (selected.kind === "template" && selected.template) {
      setReadmeMarkdown(buildTemplateReadme(selected.template));
      setReadmeLoading(false);
      return;
    }
    const projectId = selected.project?.id;
    if (!projectId) {
      setReadmeMarkdown("");
      return;
    }
    let cancelled = false;
    setReadmeLoading(true);
    void getFile(projectId, DEFAULT_README_FILE)
      .then((file) => {
        if (cancelled) return;
        setReadmeMarkdown(file?.content?.trim() ?? "");
      })
      .catch(() => {
        if (!cancelled) setReadmeMarkdown("");
      })
      .finally(() => {
        if (!cancelled) setReadmeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.id, selected?.kind, selected?.project?.id, selected?.template?.id]);

  const handleCreateFromTemplate = useCallback(
    (item: StudioCatalogItem) => {
      if (!item.templateId) return;
      onCreateFromTemplate({
        name: item.name,
        version: "1.0",
        description: item.description,
        templateId: item.templateId,
        overlay: item.overlay,
        kind: "indicator",
      });
    },
    [onCreateFromTemplate]
  );

  const handleInstall = useCallback(
    async (templateId: string) => {
      setInstallingTemplateId(templateId);
      try {
        await onInstallTemplate(templateId);
      } finally {
        setInstallingTemplateId(null);
      }
    },
    [onInstallTemplate]
  );

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
        <Button size="sm" className="gap-1.5" onClick={onNew}>
          <Plus className="h-4 w-4" />
          New indicator
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => {
            const input = document.getElementById(
              "vybe-studio-import-input"
            ) as HTMLInputElement | null;
            input?.click();
          }}
        >
          <Upload className="h-4 w-4" />
          Import
        </Button>
        <input
          id="vybe-studio-import-input"
          type="file"
          accept=".vybeind,application/json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onImportFile(file);
          }}
        />
      </div>

      <div className="flex flex-1 min-h-0">
        <aside className="flex w-[280px] shrink-0 flex-col border-r border-border bg-muted/20">
          <div className="shrink-0 space-y-2 border-b border-border p-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search indicators"
                className="h-8 pl-8 text-xs"
              />
            </div>
            <div className="flex gap-1">
              {FILTER_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const count = sectionCount(catalog, opt.id);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    title={opt.label}
                    onClick={() => setFilter(opt.id)}
                    className={cn(
                      "flex flex-1 flex-col items-center gap-0.5 rounded-md px-1 py-1.5 text-[10px] transition-colors",
                      filter === opt.id
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="truncate w-full text-center">
                      {opt.label}
                    </span>
                    <span className="text-[9px] opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <div className="p-1">
              {error ? (
                <p className="px-2 py-4 text-xs text-destructive text-center">
                  {error}
                </p>
              ) : null}
              {isLoading && filteredItems.length === 0 ? (
                <p className="px-2 py-8 text-xs text-muted-foreground text-center">
                  Loading…
                </p>
              ) : null}
              {!isLoading && filteredItems.length === 0 ? (
                <p className="px-2 py-8 text-xs text-muted-foreground text-center">
                  {filter === "marketplace"
                    ? "No templates match your search."
                    : filter === "installed"
                      ? "No built indicators. Compile a project or install from Marketplace."
                      : "No custom indicators yet."}
                </p>
              ) : (
                <ul className="space-y-0.5">
                  {filteredItems.map((item) => (
                    <CatalogListItem
                      key={item.id}
                      item={item}
                      selected={selectedId === item.id}
                      isTemplateInstalled={
                        item.templateId
                          ? installedTemplateIds.has(item.templateId)
                          : false
                      }
                      onSelect={() => {
                        setSelectedId(item.id);
                        setDetailTab("details");
                      }}
                    />
                  ))}
                </ul>
              )}
            </div>
          </ScrollArea>
        </aside>

        <main className="flex flex-1 min-h-0 min-w-0 flex-col bg-background">
          {!selected ? (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              Select an indicator to view details
            </div>
          ) : (
            <CatalogDetailsPane
              item={selected}
              detailTab={detailTab}
              onDetailTabChange={setDetailTab}
              readmeMarkdown={readmeMarkdown}
              readmeLoading={readmeLoading}
              isTemplateInstalled={
                selected.templateId
                  ? installedTemplateIds.has(selected.templateId)
                  : false
              }
              installingTemplateId={installingTemplateId}
              onAddToChart={onAddToChart}
              onOpenEditor={onOpenEditor}
              onExportSource={onExportSource}
              onExportBinary={onExportBinary}
              canExportSource={canExportSource}
              onDelete={onDelete}
              onCreateFromTemplate={handleCreateFromTemplate}
              onInstallTemplate={handleInstall}
            />
          )}
        </main>
      </div>
    </div>
  );
}

function CatalogListItem({
  item,
  selected,
  isTemplateInstalled,
  onSelect,
}: {
  item: StudioCatalogItem;
  selected: boolean;
  isTemplateInstalled: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors",
          selected
            ? "bg-primary/10 ring-1 ring-primary/30"
            : "hover:bg-muted/60"
        )}
      >
        <CustomIndicatorIcon
          name={item.name}
          iconDataUrl={item.iconDataUrl}
          size={32}
          className="h-8 w-8 shrink-0 mt-0.5 rounded-md"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium truncate">{item.name}</span>
            {item.build || isTemplateInstalled ? (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500"
                title="Installed / built"
              />
            ) : null}
          </div>
          <p className="text-[10px] text-muted-foreground line-clamp-2 leading-snug mt-0.5">
            {item.description || "No description"}
          </p>
          <p className="text-[9px] text-muted-foreground/80 mt-0.5 truncate">
            {item.kind === "template"
              ? isTemplateInstalled
                ? "Installed from Marketplace"
                : "Vybe Marketplace"
              : item.build
                ? "Installed"
                : item.project?.owner === "system"
                  ? "System"
                  : "Local project"}
          </p>
        </div>
      </button>
    </li>
  );
}

function CatalogDetailsPane({
  item,
  detailTab,
  onDetailTabChange,
  readmeMarkdown,
  readmeLoading,
  isTemplateInstalled,
  installingTemplateId,
  onAddToChart,
  onOpenEditor,
  onExportSource,
  onExportBinary,
  canExportSource,
  onDelete,
  onCreateFromTemplate,
  onInstallTemplate,
}: {
  item: StudioCatalogItem;
  detailTab: string;
  onDetailTabChange: (tab: string) => void;
  readmeMarkdown: string;
  readmeLoading: boolean;
  isTemplateInstalled: boolean;
  installingTemplateId: string | null;
  onAddToChart: (record: CustomIndicatorRecord) => void;
  onOpenEditor: (project: CustomIndicatorProject) => void;
  onExportSource: (projectId: string) => void;
  onExportBinary: (projectId: string) => void;
  canExportSource: (project: CustomIndicatorProject) => boolean;
  onDelete: (project: CustomIndicatorProject) => void;
  onCreateFromTemplate: (item: StudioCatalogItem) => void;
  onInstallTemplate: (templateId: string) => Promise<void>;
}) {
  const isTemplate = item.kind === "template";
  const project = item.project ? normalizeProject(item.project) : undefined;
  const editable = project ? isProjectEditable(project) : false;
  const showCreateFromTemplate = canShowCreateFromTemplate(item);
  const hasBuild = Boolean(item.build?.assemblyBase64);
  const inputsCount = item.build?.meta?.inputs?.length ?? 0;

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <header className="shrink-0 border-b border-border px-5 py-4">
        <div className="flex gap-4">
          <CustomIndicatorIcon
            name={item.name}
            iconDataUrl={item.iconDataUrl}
            size={56}
            className={SQUARE_ICON_CLASS}
          />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold tracking-tight truncate">
              {item.name}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isTemplate
                ? "Vybe Marketplace"
                : project
                  ? `${ownerDisplayLabel(project.owner)} · ${sourceDisplayLabel(project.source)} · v${item.version}`
                  : ""}
            </p>
            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
              {item.description}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {isTemplate && item.templateId ? (
                <>
                  <Button
                    size="sm"
                    className="gap-1.5"
                    disabled={
                      isTemplateInstalled ||
                      installingTemplateId === item.templateId
                    }
                    onClick={() => void onInstallTemplate(item.templateId!)}
                  >
                    <PackagePlus className="h-3.5 w-3.5" />
                    {isTemplateInstalled
                      ? "Installed"
                      : installingTemplateId === item.templateId
                        ? "Installing…"
                        : "Install"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={() => onCreateFromTemplate(item)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Create from template
                  </Button>
                </>
              ) : project ? (
                <>
                  {hasBuild ? (
                    <Button
                      size="sm"
                      className="gap-1.5"
                      onClick={() =>
                        onAddToChart(
                          projectToRecord(project, project.entryContent ?? "")
                        )
                      }
                    >
                      <LineChart className="h-3.5 w-3.5" />
                      Add to chart
                    </Button>
                  ) : null}
                  {editable ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => onOpenEditor(project)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Open editor
                    </Button>
                  ) : null}
                  {canExportSource(project) ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => onExportSource(project.id)}
                    >
                      <Download className="h-3.5 w-3.5" />
                      Export source
                    </Button>
                  ) : null}
                  {hasBuild ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => onExportBinary(project.id)}
                    >
                      <Download className="h-3.5 w-3.5" />
                      Export binary
                    </Button>
                  ) : null}
                  {showCreateFromTemplate && item.templateId ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      onClick={() => onCreateFromTemplate(item)}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Create from this template
                    </Button>
                  ) : null}
                  {project.owner === "user" || project.readOnly === false ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-destructive hover:text-destructive"
                      onClick={() => onDelete(project)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-destructive hover:text-destructive"
                      onClick={() => onDelete(project)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Uninstall
                    </Button>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <Tabs
        value={detailTab}
        onValueChange={onDetailTabChange}
        className="flex flex-1 min-h-0 flex-col px-5"
      >
        <TabsList className="shrink-0 mt-3 h-8 w-fit">
          <TabsTrigger value="details" className="text-xs px-3">
            Details
          </TabsTrigger>
          {inputsCount > 0 || isTemplate ? (
            <TabsTrigger value="inputs" className="text-xs px-3">
              Inputs
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="readme" className="text-xs px-3">
            README
          </TabsTrigger>
          <TabsTrigger value="preview" className="text-xs px-3">
            Preview
          </TabsTrigger>
        </TabsList>

        <div className="flex flex-1 min-h-0 gap-4 mt-2">
          <div className="flex-1 min-w-0 min-h-0">
            <TabsContent
              value="details"
              className="mt-0 h-full data-[state=inactive]:hidden"
            >
              <ScrollArea className="h-[calc(100vh-280px)] min-h-[200px] max-h-full">
                <DetailsTabContent item={item} />
              </ScrollArea>
            </TabsContent>
            <TabsContent
              value="inputs"
              className="mt-0 h-full data-[state=inactive]:hidden"
            >
              <ScrollArea className="h-[calc(100vh-280px)] min-h-[200px] max-h-full">
                <InputsTabContent item={item} />
              </ScrollArea>
            </TabsContent>
            <TabsContent
              value="readme"
              className="mt-0 h-full data-[state=inactive]:hidden"
            >
              <ScrollArea className="h-[calc(100vh-280px)] min-h-[200px] max-h-full">
                {readmeLoading ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    Loading README…
                  </p>
                ) : (
                  <StudioIndicatorReadme markdown={readmeMarkdown} />
                )}
              </ScrollArea>
            </TabsContent>
            <TabsContent
              value="preview"
              className="mt-0 h-full data-[state=inactive]:hidden"
            >
              <PreviewTabContent item={item} />
            </TabsContent>
          </div>

          <aside className="hidden lg:block w-52 shrink-0 border-l border-border pl-4 py-1">
            <MetadataRail item={item} />
          </aside>
        </div>
      </Tabs>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-1 border-b border-border/50 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground/90 break-words font-mono text-[11px]">
        {value}
      </dd>
    </div>
  );
}

function DetailsTabContent({ item }: { item: StudioCatalogItem }) {
  const project = item.project ? normalizeProject(item.project) : undefined;
  const meta = item.build?.meta;
  const build = item.build;

  return (
    <div className="space-y-4 pb-6 text-sm">
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Overview
        </h3>
        <p className="text-sm text-foreground/90 leading-relaxed">
          {item.description || "No description provided."}
        </p>
      </section>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Metadata
        </h3>
        <dl className="text-xs">
          <DetailRow label="Name" value={item.name} />
          <DetailRow label="Version" value={item.version} />
          <DetailRow
            label="Placement"
            value={item.overlay ? "Overlay on price" : "Separate pane"}
          />
          <DetailRow
            label="Kind"
            value={item.projectKind === "trading-algo" ? "Trading algo" : "Indicator"}
          />
          {project ? (
            <>
              <DetailRow label="Owner" value={ownerDisplayLabel(project.owner)} />
              <DetailRow label="Source" value={sourceDisplayLabel(project.source)} />
              <DetailRow
                label="Read-only"
                value={project.readOnly ? "Yes" : "No"}
              />
              <DetailRow
                label="Source available"
                value={project.hasSource ? "Yes" : "No"}
              />
              {project.publisher ? (
                <DetailRow label="Publisher" value={project.publisher} />
              ) : null}
              {project.sourceTemplateId ? (
                <DetailRow
                  label="Template ID"
                  value={project.sourceTemplateId}
                />
              ) : null}
              <DetailRow label="Project ID" value={project.id} />
              <DetailRow label="Size" value={formatBytes(item.sizeBytes)} />
              <DetailRow
                label="Created"
                value={formatCatalogDate(project.createdAt)}
              />
              <DetailRow
                label="Updated"
                value={formatCatalogDate(project.updatedAt)}
              />
              {project.installedAt ? (
                <DetailRow
                  label="Installed"
                  value={formatCatalogDate(project.installedAt)}
                />
              ) : null}
            </>
          ) : item.templateId ? (
            <DetailRow label="Template ID" value={item.templateId} />
          ) : null}
        </dl>
      </section>

      {build ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Build
          </h3>
          <dl className="text-xs">
            <DetailRow label="Provenance" value={formatBuildProvenance(build)} />
            <DetailRow
              label="Assembly size"
              value={formatBytes(build.sizeBytes)}
            />
            {build.runtimeBuiltAt ? (
              <DetailRow
                label="Runtime built"
                value={build.runtimeBuiltAt}
              />
            ) : null}
            {meta ? (
              <>
                <DetailRow
                  label="Plots"
                  value={String(meta.plots?.length ?? 0)}
                />
                <DetailRow
                  label="Inputs"
                  value={String(meta.inputs?.length ?? 0)}
                />
                <DetailRow
                  label="Panes"
                  value={String(meta.panes?.length ?? 0)}
                />
                <DetailRow
                  label="Signals"
                  value={String(
                    (meta.signals?.length ?? 0) + (meta.signal ? 1 : 0)
                  )}
                />
                <DetailRow
                  label="Zones"
                  value={String(meta.zone ? 1 : 0)}
                />
                <DetailRow
                  label="Dashboard"
                  value={meta.dashboard ? "Yes" : "No"}
                />
                {meta.className ? (
                  <DetailRow label="Class" value={meta.className} />
                ) : null}
                {meta.icon?.dataUrl ? (
                  <DetailRow label="Icon from build" value="Yes" />
                ) : null}
              </>
            ) : null}
          </dl>
        </section>
      ) : null}

      {item.tags.length > 0 ? (
        <section className="lg:hidden">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            Tags
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {[...new Set(item.tags)].map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px]">
                {tag}
              </Badge>
            ))}
          </div>
        </section>
      ) : null}

      <section className="lg:hidden">
        <MetadataRail item={item} />
      </section>
    </div>
  );
}

function InputsTabContent({ item }: { item: StudioCatalogItem }) {
  const inputs: CompileInputMeta[] =
    item.build?.meta?.inputs ??
    (item.kind === "template" ? [] : []);

  if (inputs.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {item.kind === "template"
          ? "Install or create a project from this template to see compiled inputs."
          : "No input metadata. Compile the indicator to populate inputs."}
      </p>
    );
  }

  const byGroup = new Map<string, CompileInputMeta[]>();
  for (const input of inputs) {
    const group = input.group?.trim() || "General";
    const list = byGroup.get(group) ?? [];
    list.push(input);
    byGroup.set(group, list);
  }

  return (
    <div className="space-y-6 pb-6">
      {[...byGroup.entries()].map(([group, groupInputs]) => (
        <section key={group}>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            {group}
          </h3>
          <div className="space-y-3">
            {groupInputs.map((input) => (
              <div
                key={input.name}
                className="rounded-md border border-border p-3 text-xs"
              >
                <div className="font-medium text-foreground">{input.label}</div>
                <div className="text-muted-foreground mt-0.5 font-mono">
                  {input.name} · {input.type}
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
                  <div>
                    <dt>Default</dt>
                    <dd className="text-foreground/90">
                      {String(input.defaultValue ?? "—")}
                    </dd>
                  </div>
                  {input.min != null ? (
                    <div>
                      <dt>Min</dt>
                      <dd className="text-foreground/90">{input.min}</dd>
                    </div>
                  ) : null}
                  {input.max != null ? (
                    <div>
                      <dt>Max</dt>
                      <dd className="text-foreground/90">{input.max}</dd>
                    </div>
                  ) : null}
                  {input.step != null ? (
                    <div>
                      <dt>Step</dt>
                      <dd className="text-foreground/90">{input.step}</dd>
                    </div>
                  ) : null}
                </dl>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function MetadataRail({ item }: { item: StudioCatalogItem }) {
  const project = item.project ? normalizeProject(item.project) : undefined;
  const ownershipTags = [
    ...new Set(
      project
        ? buildOwnershipTags(project)
        : item.kind === "template"
          ? ["indicator", "system", "marketplace", "source-available"]
          : []
    ),
  ];

  return (
    <div className="space-y-4 text-xs">
      <div>
        <h4 className="font-semibold text-foreground mb-2">Tags</h4>
        <div className="flex flex-wrap gap-1">
          {ownershipTags.map((tag) => (
            <Badge key={tag} variant="outline" className="text-[10px]">
              {ownershipBadgeLabel(tag)}
            </Badge>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-foreground mb-2">Installation</h4>
        <dl className="space-y-2 text-muted-foreground">
          <div>
            <dt className="text-[10px] uppercase tracking-wide">Identifier</dt>
            <dd className="font-mono text-[10px] break-all text-foreground/80 mt-0.5">
              {item.kind === "template" ? item.templateId : item.project?.id}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide">Version</dt>
            <dd className="text-foreground/90 mt-0.5">v{item.version}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide">Size</dt>
            <dd className="text-foreground/90 mt-0.5">
              {formatBytes(item.sizeBytes)}
            </dd>
          </div>
          {project ? (
            <>
              <div>
                <dt className="text-[10px] uppercase tracking-wide">Created</dt>
                <dd className="text-foreground/90 mt-0.5">
                  {formatCatalogDate(project.createdAt)}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wide">
                  Last updated
                </dt>
                <dd className="text-foreground/90 mt-0.5">
                  {formatCatalogDate(project.updatedAt)}
                </dd>
              </div>
            </>
          ) : null}
        </dl>
      </div>
      {item.kind === "project" ? (
        <div>
          <h4 className="font-semibold text-foreground mb-2">Build</h4>
          <p className="text-muted-foreground leading-snug">
            {formatBuildProvenance(item.build)}
          </p>
          {item.build ? (
            <p className="text-muted-foreground mt-1">
              Assembly {formatBytes(item.build.sizeBytes)}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type PreviewHydrationState = "idle" | "loading" | "ready" | "error";

function PreviewTabContent({ item }: { item: StudioCatalogItem }) {
  const projectId = item.project?.id;
  const build = item.build;
  const [hydration, setHydration] = useState<PreviewHydrationState>("idle");

  const compileResult = useIndicatorCompileStore((s) =>
    projectId ? s.byProjectId[projectId] : undefined
  );
  const compileRevision = useIndicatorCompileStore((s) =>
    projectId ? (s.revisionByProjectId[projectId] ?? 0) : 0
  );

  useEffect(() => {
    if (!projectId || !build?.assemblyBase64) {
      setHydration("idle");
      return;
    }

    let cancelled = false;
    setHydration("loading");

    void loadPersistedBuildIntoRuntime(projectId)
      .then((result) => {
        if (cancelled) return;
        setHydration(result?.success ? "ready" : "error");
      })
      .catch(() => {
        if (!cancelled) setHydration("error");
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, build?.builtAt, build?.assemblyBase64]);

  if (item.kind === "template") {
    const code = item.entryContent ?? item.template?.code ?? "";
    return (
      <div className="flex flex-col min-h-[280px] h-[calc(100vh-280px)] max-h-[520px] rounded-lg border border-border overflow-hidden">
        <div className="shrink-0 border-b border-border bg-muted/30 px-3 py-2">
          <p className="text-xs text-muted-foreground">
            Template source preview. Install or create a project to compile and
            run on a chart.
          </p>
        </div>
        <ScrollArea className="flex-1 min-h-0">
          <pre className="p-3 text-[10px] leading-relaxed font-mono text-foreground/90 whitespace-pre-wrap break-words">
            {code.slice(0, 12000)}
            {code.length > 12000 ? "\n\n… (truncated)" : ""}
          </pre>
        </ScrollArea>
      </div>
    );
  }

  const project = item.project;
  const code = project?.hasSource ? (item.entryContent ?? "") : "";
  const canPreviewChart = Boolean(projectId && item.build?.assemblyBase64);

  if (!canPreviewChart) {
    return (
      <div className="flex flex-col min-h-[200px] items-center justify-center rounded-lg border border-dashed border-border p-6 text-center">
        <p className="text-sm text-muted-foreground">
          {project?.hasSource
            ? "Compile this indicator to preview it on a chart."
            : "Binary-only install — no source preview. Add to chart when built."}
        </p>
      </div>
    );
  }

  if (hydration === "loading" || hydration === "idle") {
    return (
      <div className="flex flex-col min-h-[200px] items-center justify-center rounded-lg border border-dashed border-border p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Loading compiled indicator into runtime…
        </p>
      </div>
    );
  }

  if (hydration === "error" || !compileResult?.success) {
    return (
      <div className="flex flex-col min-h-[200px] items-center justify-center rounded-lg border border-dashed border-border p-6 text-center gap-2">
        <p className="text-sm text-muted-foreground">
          Could not load this indicator&apos;s compiled assembly. Try recompiling
          in the editor, or re-import the .vybeind bundle.
        </p>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            if (!projectId) return;
            setHydration("loading");
            void loadPersistedBuildIntoRuntime(projectId).then((result) => {
              setHydration(result?.success ? "ready" : "error");
            });
          }}
        >
          Retry load
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[320px] h-[calc(100vh-280px)] max-h-[560px] rounded-lg border border-border overflow-hidden">
      <CustomIndicatorPreviewChart
        key={`${projectId}:r${compileRevision}`}
        projectId={projectId}
        code={code}
        indicatorName={item.name}
      />
    </div>
  );
}
